import { useState, useCallback, useEffect } from "react";
import { useForm, type Resolver, type DefaultValues } from "react-hook-form";
import { useRouter } from "expo-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { step5Schema, type Step5Values } from "../schemas/preVettingSchema";
import { usePreVettingDraft } from "./usePreVettingDraft";
import { useDebouncedDraftSave } from "./useDebouncedDraftSave";
import { useRestoredDocumentFile } from "@/hooks/useRestoredDocumentFile";
import { preVettingApi } from "@/lib/api/preVetting";
import { useAuth } from "@/context/AuthContext";
import type { FileUploadState, SelectedFile } from "@/components/ui/FileUpload";
import type { TranslationKeys } from "@chinese-vendor/i18n";

export function usePreVettingStep5Form() {
  const router = useRouter();
  const { setVendorStatus } = useAuth();
  const { draftQuery, saveDraftMutation } = usePreVettingDraft();
  const scheduleSave = useDebouncedDraftSave(saveDraftMutation.mutateAsync);

  const submitMutation = useMutation({
    mutationFn: () => preVettingApi.submit(),
    onSuccess: () => {
      setVendorStatus("pending_review");
      router.replace("/(vendor)/pending-review");
    },
  });

  const form = useForm<Step5Values>({
    resolver: zodResolver(step5Schema) as Resolver<Step5Values>,
    defaultValues: {
      business_short_name_english: "",
      business_short_name_arabic: "",
      business_description_english: "",
      business_description_arabic: "",
      primary_industry: "",
      business_logo: null,
    } as DefaultValues<Step5Values>,
    mode: "onBlur",
  });

  const [logoFile, setLogoFile] = useState<SelectedFile | null>(null);
  const [logoUploadState, setLogoUploadState] = useState<FileUploadState>("idle");
  const [logoUploadError, setLogoUploadError] = useState<TranslationKeys | undefined>(undefined);

  // Restore file preview from saved S3 key on draft load
  const { file: restoredLogo, uploadState: restoredLogoState } = useRestoredDocumentFile(
    draftQuery.isSuccess ? (draftQuery.data?.step5?.business_logo ?? null) : null
  );
  useEffect(() => {
    if (restoredLogo && logoUploadState === "idle" && !logoFile) {
      setLogoFile(restoredLogo);
      setLogoUploadState(restoredLogoState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoredLogo]);

  // Populate form once draft has loaded
  useEffect(() => {
    if (draftQuery.isSuccess && draftQuery.data?.step5) {
      form.reset({
        ...form.getValues(),
        ...(draftQuery.data.step5 as Partial<Step5Values>),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftQuery.isSuccess]);

  const onFieldBlur = useCallback(() => {
    scheduleSave({ step5: form.getValues() });
  }, [scheduleSave, form]);

  const handleLogoFile = useCallback(
    async (file: SelectedFile) => {
      setLogoFile(file);
      setLogoUploadState("uploading");
      setLogoUploadError(undefined);
      try {
        const key = await preVettingApi.uploadDocument(file);
        form.setValue("business_logo", key);
        onFieldBlur();
        setLogoUploadState("success");
      } catch {
        setLogoUploadState("error");
        setLogoUploadError("fileUpload.uploadFailed");
      }
    },
    [form, onFieldBlur]
  );

  const handleLogoRemove = useCallback(() => {
    setLogoFile(null);
    setLogoUploadState("idle");
    setLogoUploadError(undefined);
    form.setValue("business_logo", null);
  }, [form]);

  const handleBack = useCallback(() => {
    router.back();
  }, [router]);

  const handleSubmit = form.handleSubmit(async (values) => {
    await saveDraftMutation.mutateAsync({ step5: values }).catch(() => {});
    await submitMutation.mutateAsync().catch(() => {});
  });

  const isSubmitting = saveDraftMutation.isPending || submitMutation.isPending;
  const submitError =
    saveDraftMutation.isError || submitMutation.isError ? "common.error.generic" : null;

  return {
    form,
    onFieldBlur,
    handleSubmit,
    handleBack,
    isSubmitting,
    submitError,
    logoFile,
    logoUploadState,
    logoUploadError,
    handleLogoFile,
    handleLogoRemove,
  };
}
